import { getAppletsUrl } from '@cartwhl/common/utils';

import { noticesStore } from 'stores/global';

import { getSceneMotionEditorPath } from './assetLibraryActions';
import type { AssetLibraryBatchGroupAssetType } from './assetLibraryGroupDetail';
import type { AssetLibraryDisplayItem } from './utils';
import { shouldShowStackedPreview } from './utils';

export const ASSET_LIBRARY_SHARE_PREVIEW_PARAM = 'preview';
export const ASSET_LIBRARY_SHARE_GROUP_PARAM = 'group';
export const ASSET_LIBRARY_SHARE_ASSET_TYPE_PARAM = 'assetType';

export function parseJobAssetTypeFromShareParam(
  value: string | null | undefined,
): AssetLibraryBatchGroupAssetType | null {
  if (value === 'MOGEN_JOB' || value === 'MASCOT_JOB') {
    return value;
  }

  return null;
}

function isNonStackedJob(asset: AssetLibraryDisplayItem): boolean {
  return (
    (asset.assetType === 'MOGEN_JOB' || asset.assetType === 'MASCOT_JOB') &&
    Boolean(asset.groupID) &&
    !shouldShowStackedPreview(asset)
  );
}

function buildLibrarySharePath(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `/library?${query}` : '/library';
}

function buildAssetLibraryJobPreviewShareUrl(input: {
  jobId: string;
  groupID: string;
  assetType: AssetLibraryBatchGroupAssetType;
}): string {
  const params = new URLSearchParams();
  params.set(ASSET_LIBRARY_SHARE_PREVIEW_PARAM, input.jobId);
  params.set(ASSET_LIBRARY_SHARE_GROUP_PARAM, input.groupID);
  params.set(ASSET_LIBRARY_SHARE_ASSET_TYPE_PARAM, input.assetType);
  return `${getAppletsUrl()}${buildLibrarySharePath(params)}`;
}

function buildAssetLibraryGroupDetailShareUrl(groupID: string): string {
  const params = new URLSearchParams();
  params.set(ASSET_LIBRARY_SHARE_GROUP_PARAM, groupID);
  return `${getAppletsUrl()}${buildLibrarySharePath(params)}`;
}

export function buildAssetLibraryShareUrl(
  asset: AssetLibraryDisplayItem,
): string | null {
  const baseUrl = getAppletsUrl();

  if (asset.assetType === 'SCENE') {
    return `${baseUrl}${getSceneMotionEditorPath(asset.assetID)}`;
  }

  if (asset.groupID && shouldShowStackedPreview(asset)) {
    return buildAssetLibraryGroupDetailShareUrl(asset.groupID);
  }

  if (isNonStackedJob(asset)) {
    return buildAssetLibraryJobPreviewShareUrl({
      jobId: asset.assetID,
      groupID: asset.groupID!,
      assetType: asset.assetType as AssetLibraryBatchGroupAssetType,
    });
  }

  if (asset.assetType === 'MEDIA_IMAGE' || asset.assetType === 'MEDIA_VIDEO') {
    const params = new URLSearchParams();
    params.set(ASSET_LIBRARY_SHARE_PREVIEW_PARAM, asset.assetID);
    return `${baseUrl}${buildLibrarySharePath(params)}`;
  }

  return null;
}

export function getAssetLibraryShareLinkResolveId(
  previewId: string | null,
  groupId: string | null,
): string | null {
  if (groupId && previewId) {
    return groupId;
  }

  return previewId ?? groupId;
}

export function hasAssetLibraryShareLinkParams(
  searchParams: URLSearchParams,
): boolean {
  return (
    searchParams.has(ASSET_LIBRARY_SHARE_PREVIEW_PARAM) ||
    searchParams.has(ASSET_LIBRARY_SHARE_GROUP_PARAM)
  );
}

export function clearAssetLibraryShareLinkParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(ASSET_LIBRARY_SHARE_PREVIEW_PARAM);
  next.delete(ASSET_LIBRARY_SHARE_GROUP_PARAM);
  next.delete(ASSET_LIBRARY_SHARE_ASSET_TYPE_PARAM);
  return next;
}

export async function copyAssetLibraryShareLink(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    noticesStore.info('Link copied to clipboard');
  } catch {
    noticesStore.error('Could not copy to clipboard');
  }
}
